import { type ReactNode } from 'react';
import { type NavV2Response, type OddsFormat, type SiteConfig } from '@cx-web/domain-types';

import { type Repub } from '@cx-modules/constants';

import { DesktopHeaderTrackingProvider } from '../../context/DesktopHeaderTrackingContext';
import { type DesktopHeaderTrackingData } from '../../types';
import { PrimaryHeaderNavGroup } from '../PrimaryHeaderNavGroup/index';
import { SecondaryHeaderNavGroup } from '../SecondaryHeaderNavGroup/index';
import { TertiaryHeaderNavGroup } from '../TertiaryHeaderNavGroup/index';

import styles from './HeaderDesktop.module.css';

type HeaderDesktopProps = {
  betslipSlot?: ReactNode;
  isLoggedIn: boolean;
  isUkStyle?: boolean;
  logoutLabel: string;
  navData: NavV2Response;
  navigationKey?: string;
  oddsFormat: OddsFormat;
  onDesktopHeaderItemClick?: (data: DesktopHeaderTrackingData) => void;
  onLogout: () => void;
  onOddsFormatChange: (format: OddsFormat) => void;
  onStateChange?: (state: string) => void;
  renderAccountMenu?: (variant: 'desktop' | 'mobile') => ReactNode;
  renderLogo?: ReactNode;
  repub?: Repub;
  repubHomeUrl: string;
  selectedState?: string;
  serverUrl: string;
  siteConfig: SiteConfig;
  stateOptions?: Array<{ code: string; label: string }>;
};

export const HeaderDesktop = ({
  betslipSlot,
  isLoggedIn,
  isUkStyle,
  logoutLabel,
  navData,
  navigationKey,
  oddsFormat,
  onDesktopHeaderItemClick,
  onLogout,
  onOddsFormatChange,
  onStateChange,
  renderAccountMenu,
  renderLogo,
  repub,
  repubHomeUrl,
  selectedState,
  serverUrl,
  siteConfig,
  stateOptions,
}: HeaderDesktopProps) => {
  return (
    <DesktopHeaderTrackingProvider onTrack={isUkStyle ? onDesktopHeaderItemClick : undefined}>
      <header className={styles.headerContainer} data-testid="header-v2">
        <PrimaryHeaderNavGroup
          // UK style hosts betslip in the secondary row only — avoid double-mount when both nav levels have BETSLIP.
          betslipSlot={isUkStyle ? undefined : betslipSlot}
          firstLevelNav={navData.firstLevelNav}
          isLoggedIn={isLoggedIn}
          isUkStyle={isUkStyle}
          logoutLabel={logoutLabel}
          mobileHamburgerNav={navData.mobileHamburgerNav}
          navigationKey={navigationKey}
          oddsFormat={oddsFormat}
          onLogout={onLogout}
          onOddsFormatChange={onOddsFormatChange}
          onStateChange={onStateChange}
          renderAccountMenu={renderAccountMenu}
          repub={repub}
          selectedState={selectedState}
          serverUrl={serverUrl}
          siteConfig={siteConfig}
          stateOptions={stateOptions}
        />
        <SecondaryHeaderNavGroup
          betslipSlot={betslipSlot}
          bookmakers={siteConfig.bookmakers}
          isUkStyle={isUkStyle}
          navigationKey={navigationKey}
          renderLogo={renderLogo}
          repubHomeUrl={repubHomeUrl}
          secondLevelNav={navData.secondLevelNav}
          serverUrl={serverUrl}
        />
        <TertiaryHeaderNavGroup
          bookmakers={siteConfig.bookmakers}
          isUkStyle={isUkStyle}
          navigationKey={navigationKey}
          serverUrl={serverUrl}
          thirdLevelNav={navData.thirdLevelNav}
        />
      </header>
    </DesktopHeaderTrackingProvider>
  );
};
